<template>
    <div v-if="editMode">
        <ModalComponent
            :is-open="isModalOpen"
            @close="closeModal"
        >
            <button
                @click="closeModal"
                class="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
            >
                <font-awesome-icon icon="fa-solid fa-xmark" />
            </button>

            <h3 class="font-bold text-lg mb-4">Locatii</h3>

            <div v-if="section.locationElements" class="loc-editor">
                <div
                    v-for="(location, locationKey) in section.locationElements"
                    :key="locationKey"
                    class="loc-card"
                >
                    <div class="loc-card__header">
                        <span class="loc-card__title">{{ location.locationTitle || 'Locatie' }}</span>
                        <div class="loc-card__actions">
                            <label class="loc-card__toggle">
                                <span class="loc-card__toggle-label">{{ location.visible ? 'Vizibil' : 'Ascuns' }}</span>
                                <input
                                    type="checkbox"
                                    class="toggle toggle-sm toggle-success"
                                    v-model="location.visible"
                                />
                            </label>
                            <button
                                class="loc-card__delete"
                                @click="removeCard(locationKey)"
                                title="Sterge acest card"
                            >
                                <font-awesome-icon icon="fa-solid fa-trash-can" />
                            </button>
                        </div>
                    </div>

                    <div class="loc-card__body">
                        <div class="loc-card__row">
                            <label class="loc-card__field">
                                <span class="loc-card__label">Titlu</span>
                                <input
                                    type="text"
                                    v-model="location.locationTitle"
                                    class="loc-card__input"
                                    placeholder="ex: Biserica, Restaurant, Sala..."
                                />
                            </label>
                        </div>

                        <div class="loc-card__row loc-card__row--half">
                            <label class="loc-card__field">
                                <span class="loc-card__label">Data / Ora</span>
                                <input
                                    type="text"
                                    v-model="location.date"
                                    class="loc-card__input"
                                    placeholder="ex: 15 August, 14:00"
                                />
                            </label>
                            <label class="loc-card__field">
                                <span class="loc-card__label">Imagine</span>
                                <div
                                    class="loc-card__img-thumb"
                                    @click="editImage(locationKey)"
                                    :style="location.image ? { backgroundImage: `url('${location.image}')` } : {}"
                                >
                                    <font-awesome-icon v-if="!location.image" icon="fa-solid fa-image" class="loc-card__img-placeholder" />
                                </div>
                            </label>
                        </div>

                        <div class="loc-card__row">
                            <label class="loc-card__field">
                                <span class="loc-card__label">Nume locatie</span>
                                <input
                                    type="text"
                                    v-model="location.locationName"
                                    class="loc-card__input"
                                    placeholder="ex: Restaurant Nunta Mare"
                                />
                            </label>
                        </div>

                        <div class="loc-card__row">
                            <label class="loc-card__field">
                                <span class="loc-card__label">Adresa</span>
                                <input
                                    type="text"
                                    v-model="location.locationAddress"
                                    class="loc-card__input"
                                    placeholder="ex: Str. Exemplu nr. 1, Oras"
                                />
                            </label>
                        </div>

                        <div class="loc-card__row">
                            <label class="loc-card__field">
                                <span class="loc-card__label">Coordonate Google Maps</span>
                                <input
                                    type="text"
                                    v-model="location.locationCoordonates"
                                    class="loc-card__input"
                                    placeholder="ex: 44.4268,26.1025"
                                />
                                <span class="loc-card__hint">Lasa gol daca nu doresti butonul de harta</span>
                            </label>
                        </div>
                    </div>
                </div>

                <button class="loc-add-btn" @click="addCard">
                    <font-awesome-icon icon="fa-solid fa-plus" />
                    <span>Adauga locatie</span>
                </button>

                <button class="loc-delete-all-btn" @click="deleteLocationElements">
                    <font-awesome-icon icon="fa-solid fa-trash-can" />
                    <span>Sterge toate locatiile</span>
                </button>
            </div>

            <div v-else class="loc-empty">
                <p class="loc-empty__text">Adauga carduri cu detalii despre locatii, cum ar fi adresa, data, ora si harta.</p>
                <button class="btn btn-primary btn-sm" @click="addLocationElements">
                    <font-awesome-icon icon="fa-solid fa-plus" />
                    Activeaza locatii
                </button>
            </div>
        </ModalComponent>

        <div class="tooltip tooltip-right" data-tip="Locatii">
            <button
                class="btn btn-square btn-sm md:btn-md md:text-lg"
                :class="{ 'btn-accent': hasLocations }"
                @click="openModal"
            >
                <font-awesome-icon icon="fa-solid fa-map-location-dot" />
            </button>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import ModalComponent from '@/components/ModalComponent.vue';

export default {
    props: {
        section: {
            type: Object,
            required: true,
        },
        sectionKey: {
            type: String,
            required: true,
        },
    },
    components: {
        ModalComponent,
    },
    data() {
        return {
            isModalOpen: false,
        };
    },
    computed: {
        ...mapGetters(['isEditMode']),
        editMode() {
            return this.isEditMode;
        },
        hasLocations() {
            return this.section.locationElements && Object.keys(this.section.locationElements).length > 0;
        },
    },
    methods: {
        openModal() {
            this.isModalOpen = true;
        },
        closeModal() {
            this.isModalOpen = false;
        },
        editImage(locationKey) {
            this.$emit(
                'edit-image',
                this.sectionKey,
                'locationElements',
                'image',
                locationKey
            );
        },
        generateKey() {
            const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
            let key = 'loc_';
            for (let i = 0; i < 6; i++) {
                key += chars.charAt(Math.floor(Math.random() * chars.length));
            }
            return key;
        },
        createBlankCard() {
            return {
                visible: true,
                image: '',
                date: '',
                locationTitle: '',
                locationName: '',
                locationAddress: '',
                locationCoordonates: '',
            };
        },
        addCard() {
            const key = this.generateKey();
            const updated = { ...this.section };
            if (!updated.locationElements) {
                updated.locationElements = {};
            }
            updated.locationElements[key] = this.createBlankCard();
            this.$emit('update-section', this.sectionKey, updated);
        },
        removeCard(locationKey) {
            const updated = { ...this.section };
            const elements = { ...updated.locationElements };
            delete elements[locationKey];
            if (Object.keys(elements).length === 0) {
                delete updated.locationElements;
            } else {
                updated.locationElements = elements;
            }
            this.$emit('update-section', this.sectionKey, updated);
        },
        deleteLocationElements() {
            const updatedSection = { ...this.section };
            delete updatedSection.locationElements;
            this.$emit('update-section', this.sectionKey, updatedSection);
        },
        addLocationElements() {
            const locationElements = {};
            locationElements[this.generateKey()] = this.createBlankCard();
            this.$emit('update-section', this.sectionKey, {
                ...this.section,
                locationElements,
            });
            this.openModal();
        },
    },
};
</script>

<style scoped>
.loc-editor {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.loc-card {
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    overflow: hidden;
}
.loc-card__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 14px;
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
}
.loc-card__title {
    font-weight: 600;
    font-size: 14px;
    color: #334155;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 140px;
}
.loc-card__actions {
    display: flex;
    align-items: center;
    gap: 10px;
}
.loc-card__toggle {
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
}
.loc-card__toggle-label {
    font-size: 11px;
    color: #64748b;
    font-weight: 500;
}
.loc-card__delete {
    background: none;
    border: none;
    color: #ef4444;
    cursor: pointer;
    padding: 4px;
    font-size: 14px;
    border-radius: 6px;
    transition: background 0.15s;
}
.loc-card__delete:hover {
    background: #fef2f2;
}

.loc-card__body {
    padding: 12px 14px;
    display: flex;
    flex-direction: column;
    gap: 10px;
}
.loc-card__row {
    display: flex;
    gap: 10px;
}
.loc-card__row--half > .loc-card__field {
    flex: 1;
    min-width: 0;
}
.loc-card__field {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
}
.loc-card__label {
    font-size: 11px;
    font-weight: 600;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}
.loc-card__input {
    height: 38px;
    padding: 0 10px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    font-size: 13px;
    color: #1e293b;
    background: #fff;
    transition: border-color 0.15s;
    width: 100%;
}
.loc-card__input:focus {
    outline: none;
    border-color: #3b82f6;
}
.loc-card__input::placeholder {
    color: #cbd5e1;
}
.loc-card__hint {
    font-size: 10px;
    color: #94a3b8;
}
.loc-card__img-thumb {
    height: 38px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background-size: cover;
    background-position: center;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.15s;
}
.loc-card__img-thumb:hover {
    opacity: 0.7;
}
.loc-card__img-placeholder {
    color: #cbd5e1;
    font-size: 16px;
}

.loc-add-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    width: 100%;
    padding: 10px;
    border: 2px dashed #cbd5e1;
    border-radius: 10px;
    background: transparent;
    color: #64748b;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: border-color 0.15s, color 0.15s;
}
.loc-add-btn:hover {
    border-color: #3b82f6;
    color: #3b82f6;
}

.loc-delete-all-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    width: 100%;
    padding: 8px;
    border: none;
    border-radius: 8px;
    background: #fef2f2;
    color: #dc2626;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s;
}
.loc-delete-all-btn:hover {
    background: #fee2e2;
}

.loc-empty {
    text-align: center;
    padding: 20px 10px;
}
.loc-empty__text {
    font-size: 13px;
    color: #64748b;
    margin-bottom: 16px;
    line-height: 1.5;
}
</style>
